import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Button,
	Collapse,
	Empty,
	Modal,
	notification,
	Spin,
	Table,
	Typography,
} from 'antd';
import {
	CheckCircleFilled,
	CloseCircleFilled,
	DeleteOutlined,
	DownloadOutlined,
	EyeOutlined,
	ClockCircleOutlined,
	PlusCircleOutlined,
} from '@ant-design/icons';
import { DatePicker } from '@hydrodigital/design-system';
import dayjs from 'dayjs';
import KeplerService from '@/domain/Kepler/KeplerService';
import './SewerAnalysisLauncherModal.scss';

const keplerService = new KeplerService();

type SewerAnalysisRecord = {
	id: string;
	keplerAnimationId?: string | null;
	panelKey: string;
	title: string;
	createdAt?: string;
	htmlUrl?: string;
	link?: string;
	excelLink?: string | null;
	status?: string;
	progress?: number;
	parameters: Array<{
		key: string;
		parameter: string;
		value: string;
	}>;
};

interface Props {
	open: boolean;
	onCancel: () => void;
	onCreateNewAnalysis: () => void;
	selectedLocation?: number | string | null;
	selectedNetworkType?: string | null;
	trackedAnalysisId?: string | null;
	trackedAnalysisName?: string | null;
	refreshKey?: number;
	shouldPollHistorical?: boolean;
	mode?: 'modal' | 'panel';
}

type DateRangeValue = {
	from?: Date;
	to?: Date;
};

const buildDatePickerValue = (range: DateRangeValue): DateRangeValue => ({
	from: range?.from ? new Date(range.from) : undefined,
	to: range?.to ? new Date(range.to) : undefined,
});

const normalizeDatePickerRange = (value: any): DateRangeValue => ({
	from: value?.from ? ((value.from as any).toDate?.() ?? new Date(value.from)) : undefined,
	to: value?.to ? ((value.to as any).toDate?.() ?? new Date(value.to)) : undefined,
});

const getDefaultPeriodRange = (): DateRangeValue => ({
	from: dayjs().subtract(3, 'month').toDate(),
	to: dayjs().toDate(),
});

const humanizeKey = (value: string) =>
	String(value || '')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, (char) => char.toUpperCase());

const formatDisplayValue = (value: unknown) => {
	if (value == null || value === '') return '';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (Array.isArray(value)) return value.join(', ');
	if (typeof value === 'object') return JSON.stringify(value);
	return String(value);
};

const getResponseItems = (response: any): any[] => {
	if (Array.isArray(response)) return response;
	if (Array.isArray(response?.data)) return response.data;
	if (Array.isArray(response?.items)) return response.items;
	if (Array.isArray(response?.records)) return response.records;
	if (Array.isArray(response?.content)) return response.content;
	return [];
};

const getRecordExcelUrl = (record: Pick<SewerAnalysisRecord, 'excelLink'>) => {
	const value = record.excelLink;
	if (typeof value !== 'string') return null;
	const trimmed = value.trim();
	if (!trimmed) return null;
	try {
		const parsed = new URL(trimmed);
		if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return trimmed;
	} catch {
		// invalid URL
	}
	return null;
};

const getRecordViewUrl = (record: Pick<SewerAnalysisRecord, 'htmlUrl' | 'link'>) => {
	const candidates = [record.htmlUrl, record.link];

	for (const candidate of candidates) {
		if (typeof candidate !== 'string') continue;

		const trimmedValue = candidate.trim();
		if (!trimmedValue) continue;
		if (trimmedValue.toLowerCase() === 'no output files found in the specified range') continue;

		try {
			const parsedUrl = new URL(trimmedValue);
			if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
				return trimmedValue;
			}
		} catch (_error) {
			continue;
		}
	}

	return null;
};

const formatRecordDateTime = (value?: string) => {
	if (!value) return null;

	const parsedValue = dayjs(value);
	if (!parsedValue.isValid()) return null;

	return parsedValue.format('YYYY-MM-DD h:mma');
};

const getRecordSortTimestamp = (record?: Pick<SewerAnalysisRecord, 'createdAt'> | null) => {
	if (!record?.createdAt) return Number.NEGATIVE_INFINITY;

	const parsedValue = dayjs(record.createdAt);
	if (!parsedValue.isValid()) return Number.NEGATIVE_INFINITY;

	return parsedValue.valueOf();
};

const sortRecordsNewestFirst = (items: SewerAnalysisRecord[]) =>
	[...items].sort((left, right) => {
		const timestampDiff = getRecordSortTimestamp(right) - getRecordSortTimestamp(left);
		if (timestampDiff !== 0) return timestampDiff;

		return right.panelKey.localeCompare(left.panelKey);
	});

const getPreferredOpenRecord = (
	items: SewerAnalysisRecord[],
	trackedAnalysisId?: string | null,
	trackedAnalysisName?: string | null,
) =>
	items.find(
		(record) =>
			(trackedAnalysisId && record.id === trackedAnalysisId) ||
			(normalizeModelName(trackedAnalysisName) &&
				normalizeModelName(record.title) === normalizeModelName(trackedAnalysisName)),
	) ??
	items.find((record) => isRecordInProgress(record)) ??
	sortRecordsNewestFirst(items)[0] ??
	null;

const normalizeModelName = (value?: unknown) =>
	typeof value === 'string' ? value.trim().toLowerCase() : '';

const normalizeParameters = (record: any) => {
	const parameterSource =
		record?.parameters ??
		record?.details ??
		record?.values ??
		record?.configuration ??
		record?.result;

	if (Array.isArray(parameterSource)) {
		return parameterSource.map((item: any, index: number) => ({
			key: String(item?.id ?? item?.key ?? item?.parameter ?? item?.name ?? index),
			parameter: String(
				item?.parameter ??
					item?.label ??
					item?.name ??
					item?.parameterName ??
					humanizeKey(item?.key ?? `item-${index}`),
			),
			value: formatDisplayValue(
				item?.value ?? item?.parameterValue ?? item?.result ?? item?.labelValue,
			),
		}));
	}

	if (parameterSource && typeof parameterSource === 'object') {
		return Object.entries(parameterSource).map(([key, value]) => ({
			key,
			parameter: humanizeKey(key),
			value: formatDisplayValue(value),
		}));
	}

	const fallbackGroups = ['constraints', 'sensorPlacement', 'opportunities']
		.map((groupKey) => {
			const groupValue = record?.[groupKey];
			if (!groupValue || typeof groupValue !== 'object') return [];
			return [
				{
					key: `${groupKey}-title`,
					parameter: humanizeKey(groupKey),
					value: '',
				},
				...Object.entries(groupValue).map(([key, value]) => ({
					key: `${groupKey}-${key}`,
					parameter: humanizeKey(key),
					value: formatDisplayValue(value),
				})),
			];
		})
		.flat();

	return fallbackGroups;
};

const formatRecordTitle = (record: any) => {
	const rawDate =
		record?.createdAt ??
		record?.creationDate ??
		record?.date ??
		record?.analysisDate ??
		record?.requestedAt ??
		record?.startTime ??
		record?.endTime ??
		record?.timestamp;
	const parsedDate = rawDate ? dayjs(rawDate) : null;

	if (parsedDate?.isValid()) {
		return parsedDate.format('D MMMM YYYY [at] HH:mm');
	}

	return String(
		record?.title ??
			record?.modelName ??
			record?.name ??
			record?.id ??
			record?.modelId ??
			'Analysis',
	);
};

const extractAnalysisRecordId = (record: any) => {
	const rawValue =
		record?.id ??
		record?.modelId ??
		record?.analysisId ??
		record?.iterationId ??
		record?.keplerAnimationId ??
		record?.data?.id ??
		record?.data?.modelId ??
		record?.data?.analysisId ??
		record?.data?.iterationId ??
		record?.data?.keplerAnimationId;

	return rawValue != null ? String(rawValue) : null;
};

const extractRecordCreatedAt = (record: any) =>
	record?.createdAt ??
	record?.creationDate ??
	record?.date ??
	record?.analysisDate ??
	record?.requestedAt ??
	record?.startTime ??
	record?.endTime ??
	record?.timestamp;

const normalizeRecordStatus = (record: any) => {
	const rawStatus = String(
		record?.status ??
			record?.analysisStatus ??
			record?.executionStatus ??
			record?.summary?.status ??
			'',
	).toUpperCase();

	if (rawStatus === 'IN PROGRESS' || rawStatus === 'IN_PROGRESS') {
		return 'IN_PROGRESS';
	}

	if (rawStatus === 'COMPLETE' || rawStatus === 'COMPLETED') {
		return 'COMPLETED';
	}

	if (rawStatus === 'FAILED') {
		return 'FAILED';
	}

	return record?.htmlUrl ?? record?.url ?? record?.html_url ?? record?.link ? 'COMPLETED' : undefined;
};

const extractProgressNumber = (value: unknown) => {
	if (typeof value === 'number' && Number.isFinite(value)) {
		const normalizedValue = value > 0 && value <= 1 ? value * 100 : value;
		return Math.max(0, Math.min(100, Math.round(normalizedValue)));
	}

	if (typeof value === 'string') {
		const trimmedValue = value.trim();
		if (!trimmedValue) return null;

		if (Number.isFinite(Number(trimmedValue))) {
			const numericValue = Number(trimmedValue);
			const normalizedValue = numericValue > 0 && numericValue <= 1 ? numericValue * 100 : numericValue;
			return Math.max(0, Math.min(100, Math.round(normalizedValue)));
		}

		const percentageMatch = trimmedValue.match(/(\d+(?:\.\d+)?)\s*%/);
		if (percentageMatch && Number.isFinite(Number(percentageMatch[1]))) {
			return Math.max(0, Math.min(100, Math.round(Number(percentageMatch[1]))));
		}
	}

	return null;
};

const normalizeRecordProgress = (record: any, status?: string) => {
	const values = [
		record?.progress,
		record?.percentage,
		record?.percent,
		record?.analysisProgress,
		record?.summary?.progress,
		record?.summary?.percentage,
		record?.summary?.percent,
		record?.statusMessage,
		record?.message,
	];

	for (const value of values) {
		const normalizedValue = extractProgressNumber(value);
		if (normalizedValue != null) return normalizedValue;
	}

	return status === 'COMPLETED' ? 100 : 0;
};

const getRunningProgressValue = (
	status: string | undefined,
	reportedProgress: number,
	currentProgress?: number,
) => {
	if (status === 'COMPLETED') return 100;
	if (status !== 'IN_PROGRESS') return reportedProgress;
	if (reportedProgress > 0) return reportedProgress;

	const baseProgress = currentProgress && currentProgress > 0 ? currentProgress : 8;
	return Math.min(baseProgress + 8, 92);
};

const buildNormalizedRecord = (
	record: any,
	fallbackId: string,
	currentProgress?: number,
): SewerAnalysisRecord => {
	const status = normalizeRecordStatus(record);
	const reportedProgress = normalizeRecordProgress(record, status);
	const id = extractAnalysisRecordId(record) ?? fallbackId;
	const createdAt = extractRecordCreatedAt(record);
	const title = formatRecordTitle(record);
	const panelKey = [
		id,
		record?.iteration ?? record?.data?.iteration ?? '',
		createdAt ?? '',
		record?.link ?? record?.htmlUrl ?? record?.url ?? record?.html_url ?? '',
		title,
	].join('::');

	return {
		id,
		keplerAnimationId: record?.keplerAnimationId ?? record?.data?.keplerAnimationId ?? null,
		panelKey,
		title,
		createdAt,
		htmlUrl: record?.htmlUrl ?? record?.url ?? record?.html_url ?? record?.link,
		link: record?.link ?? record?.htmlUrl ?? record?.url ?? record?.html_url,
		excelLink: record?.excelLink ?? null,
		status,
		progress: getRunningProgressValue(status, reportedProgress, currentProgress),
		parameters: normalizeParameters(record),
	};
};

const isTemporaryTrackedRecord = (record?: SewerAnalysisRecord | null) =>
	Boolean(record?.panelKey?.startsWith('pending::') || record?.panelKey?.startsWith('failed::'));

const findTrackedRecordIndex = (
	current: SewerAnalysisRecord[],
	incomingRecord: SewerAnalysisRecord,
	recordIds: Array<string | null | undefined>,
) => {
	const normalizedIds = recordIds.filter((value): value is string => Boolean(value));
	const exactPanelKeyIndex = current.findIndex((record) => record.panelKey === incomingRecord.panelKey);
	if (exactPanelKeyIndex >= 0) return exactPanelKeyIndex;

	const uniqueCandidateIds = [incomingRecord.id, ...normalizedIds].filter(
		(value, index, values): value is string => Boolean(value) && values.indexOf(value) === index,
	);

	for (const candidateId of uniqueCandidateIds) {
		const matchingIndexes = current.reduce<number[]>((acc, record, index) => {
			if (record.id === candidateId) {
				acc.push(index);
			}
			return acc;
		}, []);

		if (matchingIndexes.length === 1) {
			return matchingIndexes[0];
		}
	}

	const temporaryTrackedIndex = current.findIndex(
		(record) =>
			normalizedIds.includes(record.id) && (isTemporaryTrackedRecord(record) || isRecordInProgress(record)),
	);
	if (temporaryTrackedIndex >= 0) return temporaryTrackedIndex;

	return current.findIndex((record) => isRecordInProgress(record));
};

const mergeTrackedRecord = (
	current: SewerAnalysisRecord[],
	incomingRecord: SewerAnalysisRecord,
	recordIds: Array<string | null | undefined>,
) => {
	const normalizedIds = recordIds.filter((value): value is string => Boolean(value));
	const existingIndex = findTrackedRecordIndex(current, incomingRecord, [
		incomingRecord.id,
		...normalizedIds,
	]);

	if (existingIndex >= 0) {
		const existingRecord = current[existingIndex];
		const mergedRecord: SewerAnalysisRecord = {
			...existingRecord,
			...incomingRecord,
			id: incomingRecord.id || existingRecord.id,
			status: incomingRecord.status ?? existingRecord.status,
			progress: incomingRecord.progress ?? existingRecord.progress,
			parameters: incomingRecord.parameters.length
				? incomingRecord.parameters
				: existingRecord.parameters,
		};

		return current.reduce<SewerAnalysisRecord[]>((acc, record, index) => {
			if (index === existingIndex) {
				acc.push(mergedRecord);
				return acc;
			}

			if (record.panelKey === mergedRecord.panelKey) {
				return acc;
			}

			if (
				(record.id === mergedRecord.id || normalizedIds.includes(record.id)) &&
				(isTemporaryTrackedRecord(record) || isRecordInProgress(record))
			) {
				return acc;
			}

			acc.push(record);
			return acc;
		}, []);
	}

	return [
		incomingRecord,
		...current.filter(
			(record) =>
				record.panelKey !== incomingRecord.panelKey &&
				!(
					(record.id === incomingRecord.id || normalizedIds.includes(record.id)) &&
					(isTemporaryTrackedRecord(record) || isRecordInProgress(record))
				),
		),
	];
};

const normalizeRecords = (response: any): SewerAnalysisRecord[] =>
	getResponseItems(response).map((record: any, index: number) => ({
		id: extractAnalysisRecordId(record) ?? String(record?.iteration ?? index),
		keplerAnimationId: record?.keplerAnimationId ?? record?.data?.keplerAnimationId ?? null,
		panelKey: [
			extractAnalysisRecordId(record) ?? String(record?.iteration ?? index),
			record?.iteration ?? record?.data?.iteration ?? index,
			extractRecordCreatedAt(record) ?? '',
			record?.link ?? record?.htmlUrl ?? record?.url ?? record?.html_url ?? '',
			formatRecordTitle(record),
		].join('::'),
		title: formatRecordTitle(record),
		createdAt: extractRecordCreatedAt(record),
		htmlUrl: record?.htmlUrl ?? record?.url ?? record?.html_url ?? record?.link,
		link: record?.link ?? record?.htmlUrl ?? record?.url ?? record?.html_url,
		excelLink: record?.excelLink ?? null,
		status: normalizeRecordStatus(record),
		progress: normalizeRecordProgress(record, normalizeRecordStatus(record)),
		parameters: normalizeParameters(record),
	}));

const isRecordInProgress = (record?: SewerAnalysisRecord | null) => record?.status === 'IN_PROGRESS';
const shouldShowAnalysisCompletedMessage = (record?: SewerAnalysisRecord | null) =>
	record?.status === 'COMPLETED' && (record?.progress ?? 0) >= 100;
const shouldShowAnalysisFailedMessage = (record?: SewerAnalysisRecord | null) =>
	record?.status === 'FAILED';

const buildPendingAnalysisRecord = (id: string, title?: string | null): SewerAnalysisRecord => ({
	id,
	panelKey: `pending::${id}`,
	title: title?.trim() || dayjs().format('D MMMM YYYY [at] HH:mm'),
	status: 'IN_PROGRESS',
	progress: 0,
	parameters: [],
});

const buildFailedAnalysisRecord = (id: string): SewerAnalysisRecord => ({
	id,
	panelKey: `failed::${id}::${dayjs().toISOString()}`,
	title: dayjs().format('D MMMM YYYY [at] HH:mm'),
	createdAt: dayjs().toISOString(),
	status: 'FAILED',
	progress: 0,
	parameters: [],
});

const normalizeCollapseKey = (value: string | string[] | undefined) => {
	if (Array.isArray(value)) return value[0];
	return value;
};

const getRecordPanelKey = (
	record?: Pick<SewerAnalysisRecord, 'panelKey' | 'id' | 'createdAt' | 'title'> | null,
) => {
	if (!record) return '';

	return record.panelKey || [record.id, record.createdAt ?? '', record.title].join('::');
};

const matchesTrackedAnalysis = (
	record: any,
	trackedAnalysisId?: string | null,
	trackedAnalysisName?: string | null,
) => {
	const normalizedTrackedName = normalizeModelName(trackedAnalysisName);
	const recordId = extractAnalysisRecordId(record);
	const recordModelName = normalizeModelName(record?.modelName ?? record?.name ?? record?.title);

	if (trackedAnalysisId && recordId === trackedAnalysisId) {
		return true;
	}

	if (normalizedTrackedName && recordModelName === normalizedTrackedName) {
		return true;
	}

	return false;
};

const getHistoricalRecordPriority = (record: any) => {
	const status = normalizeRecordStatus(record);
	const rawDate = extractRecordCreatedAt(record);
	const parsedDate = rawDate ? dayjs(rawDate) : null;
	const createdAtPriority = parsedDate?.isValid() ? parsedDate.valueOf() : Number.NEGATIVE_INFINITY;
	const rawIteration = Number(record?.iteration ?? record?.data?.iteration);
	const iterationPriority = Number.isFinite(rawIteration) ? rawIteration : Number.NEGATIVE_INFINITY;

	if (status === 'IN_PROGRESS') {
		return { statusPriority: 0, createdAtPriority, iterationPriority };
	}

	if (status === 'COMPLETED') {
		return { statusPriority: 1, createdAtPriority, iterationPriority };
	}

	if (status === 'FAILED') {
		return { statusPriority: 2, createdAtPriority, iterationPriority };
	}

	return { statusPriority: 3, createdAtPriority, iterationPriority };
};

const selectPrioritizedHistoricalRecord = (
	items: any[],
	trackedAnalysisId?: string | null,
	trackedAnalysisName?: string | null,
) => {
	const matchingItems = items.filter((item) =>
		matchesTrackedAnalysis(item, trackedAnalysisId, trackedAnalysisName),
	);
	const candidateItems = matchingItems.length ? matchingItems : items;

	return (
		[...candidateItems].sort((left, right) => {
			const leftPriority = getHistoricalRecordPriority(left);
			const rightPriority = getHistoricalRecordPriority(right);

			if (leftPriority.statusPriority !== rightPriority.statusPriority) {
				return leftPriority.statusPriority - rightPriority.statusPriority;
			}

			if (leftPriority.createdAtPriority !== rightPriority.createdAtPriority) {
				return rightPriority.createdAtPriority - leftPriority.createdAtPriority;
			}

			if (leftPriority.iterationPriority !== rightPriority.iterationPriority) {
				return rightPriority.iterationPriority - leftPriority.iterationPriority;
			}

			return 0;
		})[0] ?? null
	);
};

export default function SewerAnalysisLauncherModal({
	open,
	onCancel,
	onCreateNewAnalysis,
	selectedLocation,
	selectedNetworkType,
	trackedAnalysisId,
	trackedAnalysisName,
	refreshKey,
	shouldPollHistorical = false,
	mode = 'modal',
}: Props) {
	const normalizedNetworkType = String(selectedNetworkType ?? '').toUpperCase();
	const isWaterAnalysis = normalizedNetworkType === 'WATER';
	const analysisTitle = isWaterAnalysis ? 'Water analysis' : 'Sewer analysis';
	const analysisLabelLower = isWaterAnalysis ? 'water analysis' : 'sewer analysis';
	const [selectedPeriodRange, setSelectedPeriodRange] = useState<DateRangeValue>(getDefaultPeriodRange);
	const [records, setRecords] = useState<SewerAnalysisRecord[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [activeCollapseKey, setActiveCollapseKey] = useState<string | undefined>(undefined);
	const [deletingRecordId, setDeletingRecordId] = useState<string | null>(null);
	const hasAutoOpenedDefaultRecordRef = useRef(false);
	const datePickerDefaultValue = useMemo(
		() => buildDatePickerValue(selectedPeriodRange),
		[selectedPeriodRange],
	);
	const datePickerResetKey = useMemo(
		() =>
			`${selectedPeriodRange.from?.getTime?.() ?? ''}-${selectedPeriodRange.to?.getTime?.() ?? ''}`,
		[selectedPeriodRange],
	);

	useEffect(() => {
		if (!open) return;

		hasAutoOpenedDefaultRecordRef.current = false;
		setRecords([]);
		setActiveCollapseKey(undefined);
		setDeletingRecordId(null);
		setSelectedPeriodRange(getDefaultPeriodRange());
		setIsLoading(Boolean(selectedLocation));
	}, [open, selectedLocation, selectedNetworkType]);

	useEffect(() => {
		if (!open || !selectedLocation) return;

		let cancelled = false;
		let pollIntervalId: number | null = null;
		const pendingRecordId = refreshKey != null && refreshKey > 0 ? `pending-${refreshKey}` : null;
		const shouldTrackHistorical = Boolean(
			shouldPollHistorical && (trackedAnalysisId != null || pendingRecordId),
		);

		if (pendingRecordId) {
			setRecords((current) => {
				if (current.some((record) => record.id === pendingRecordId)) {
					return current;
				}

				return [
					buildPendingAnalysisRecord(pendingRecordId, trackedAnalysisName),
					...current.filter((record) => !(isTemporaryTrackedRecord(record) && isRecordInProgress(record))),
				];
			});
			setActiveCollapseKey(
				getRecordPanelKey(buildPendingAnalysisRecord(pendingRecordId, trackedAnalysisName)),
			);
		}

		const stopPolling = () => {
			if (pollIntervalId !== null) {
				window.clearInterval(pollIntervalId);
				pollIntervalId = null;
			}
		};

		const fetchSewerAnalysis = async (showSpinner = false, clearBeforeHistorical = false) => {
			try {
				if (showSpinner) {
					setIsLoading(true);
				}
				if (shouldTrackHistorical) {
					if (clearBeforeHistorical) {
						setRecords((current) =>
							current.filter((record) => record.id === pendingRecordId),
						);
					}
					const historicalResponse = await keplerService.getKeplerHistorical(selectedLocation);
					if (cancelled) return;

					const historicalItems = getResponseItems(historicalResponse);
					const shouldStopHistoricalPolling = historicalItems.length === 0;
					const hasHistoricalInProgressItems = historicalItems.some(
						(item) => normalizeRecordStatus(item) === 'IN_PROGRESS',
					);
					const shouldFinalizeHistoricalPolling =
						shouldStopHistoricalPolling || !hasHistoricalInProgressItems;
					const prioritizedHistoricalRecord = selectPrioritizedHistoricalRecord(
						historicalItems,
						trackedAnalysisId,
						trackedAnalysisName,
					);

					let historicalStatus: string | undefined;
					let isHistoricalCompleted = false;
					let nextRecords: SewerAnalysisRecord[] = [];

					setRecords((current) => {
						nextRecords = current;

						if (prioritizedHistoricalRecord) {
							const historicalRecord = buildNormalizedRecord(
								prioritizedHistoricalRecord,
								pendingRecordId ?? trackedAnalysisId ?? 'historical-record',
							);
							const historicalId = historicalRecord.id;
							historicalStatus = historicalRecord.status;
							const reportedHistoricalProgress = historicalRecord.progress ?? 0;
							isHistoricalCompleted = historicalStatus === 'COMPLETED';

							if (historicalId) {
								const existingIndex = findTrackedRecordIndex(nextRecords, historicalRecord, [
									historicalId,
									trackedAnalysisId,
									pendingRecordId,
								]);
								if (existingIndex >= 0) {
									const existingRecord = nextRecords[existingIndex];
									nextRecords = mergeTrackedRecord(
										nextRecords,
										{
											...historicalRecord,
											progress: getRunningProgressValue(
												isHistoricalCompleted
													? 'COMPLETED'
													: historicalStatus ?? existingRecord.status,
												reportedHistoricalProgress,
												existingRecord.progress,
											),
										},
										[trackedAnalysisId, pendingRecordId],
									);
								} else {
									nextRecords = [
										{
											...historicalRecord,
											progress: getRunningProgressValue(
												isHistoricalCompleted ? 'COMPLETED' : historicalStatus,
												reportedHistoricalProgress,
											),
											parameters: [],
										},
										...nextRecords,
									];
								}
							}
						}

						const otherHistoricalItems = historicalItems.filter(
							(item) => item !== prioritizedHistoricalRecord,
						);
						if (otherHistoricalItems.length > 0) {
							const otherRecords = normalizeRecords(otherHistoricalItems).filter(
								(r) => !nextRecords.some((existing) => existing.panelKey === r.panelKey),
							);
							nextRecords = [...nextRecords, ...otherRecords];
						}

						if (
							pendingRecordId &&
							!shouldStopHistoricalPolling &&
							!(prioritizedHistoricalRecord && historicalStatus === 'COMPLETED') &&
							!(
								(trackedAnalysisId &&
									nextRecords.some(
										(record) =>
											record.id === trackedAnalysisId && !isTemporaryTrackedRecord(record),
									)) ||
								nextRecords.some((record) => record.id === pendingRecordId) ||
								nextRecords.some((record) => isRecordInProgress(record) && !isTemporaryTrackedRecord(record))
							)
						) {
							nextRecords = [
								buildPendingAnalysisRecord(pendingRecordId, trackedAnalysisName),
								...nextRecords,
							];
						}

						if (shouldStopHistoricalPolling && pendingRecordId) {
							const completedPendingRecordExists = nextRecords.some(
								(record) => record.id === pendingRecordId,
							);
							if (completedPendingRecordExists) {
								nextRecords = nextRecords.map((record) =>
									record.id === pendingRecordId
										? {
												...record,
												status: 'COMPLETED',
												progress: 100,
										  }
										: record,
								);
							} else {
								nextRecords = [
									{
										...buildPendingAnalysisRecord(pendingRecordId, trackedAnalysisName),
										status: 'COMPLETED',
										progress: 100,
									},
									...nextRecords,
								];
							}
							stopPolling();
						}

						return nextRecords;
					});

					if (cancelled) return;

					const prioritizedRecord = getPreferredOpenRecord(
						nextRecords,
						trackedAnalysisId,
						trackedAnalysisName,
					);

					if (prioritizedRecord) {
						setActiveCollapseKey(getRecordPanelKey(prioritizedRecord));
					}

					if (shouldFinalizeHistoricalPolling) {
						stopPolling();
						setRecords([]);

						const sewerResponse = await keplerService.getSewerAnalysis(selectedLocation);
						if (cancelled) return;

						const completedRecords = normalizeRecords(sewerResponse);
						setRecords(completedRecords);

						const prioritizedCompletedRecord = getPreferredOpenRecord(
							completedRecords,
							trackedAnalysisId,
							trackedAnalysisName,
						);

						if (prioritizedCompletedRecord) {
							setActiveCollapseKey(getRecordPanelKey(prioritizedCompletedRecord));
						}

						return;
					}

					if (hasHistoricalInProgressItems && pollIntervalId === null) {
						pollIntervalId = window.setInterval(() => {
							void fetchSewerAnalysis(false);
						}, 5000);
					}

					return;
				}

				if (showSpinner) setRecords([]);
				const sewerResponse = await keplerService.getSewerAnalysis(selectedLocation);
				if (cancelled) return;

				let nextRecords = normalizeRecords(sewerResponse);

				setRecords(nextRecords);

				const prioritizedRecord = getPreferredOpenRecord(
					nextRecords,
					trackedAnalysisId,
					trackedAnalysisName,
				);

				if (prioritizedRecord) {
					setActiveCollapseKey(getRecordPanelKey(prioritizedRecord));
				}

				if (nextRecords.some((record) => isRecordInProgress(record))) {
					if (pollIntervalId === null) {
						pollIntervalId = window.setInterval(() => {
							void fetchSewerAnalysis(false);
						}, 5000);
					}
				} else {
					stopPolling();
				}
			} catch (error) {
				if (cancelled) return;
				const errorStatus = (error as any)?.response?.status;

				if (shouldTrackHistorical && errorStatus === 500 && pendingRecordId) {
					stopPolling();
					setRecords((current) =>
						mergeTrackedRecord(
							current,
							{
								...buildFailedAnalysisRecord(pendingRecordId),
								title:
									current.find((record) => record.id === pendingRecordId || isRecordInProgress(record))
										?.title ?? buildFailedAnalysisRecord(pendingRecordId).title,
								createdAt:
									current.find((record) => record.id === pendingRecordId || isRecordInProgress(record))
										?.createdAt ?? buildFailedAnalysisRecord(pendingRecordId).createdAt,
							},
							[trackedAnalysisId, pendingRecordId],
						),
					);
					setActiveCollapseKey(getRecordPanelKey(buildFailedAnalysisRecord(pendingRecordId)));
					return;
				}

				setRecords([]);
				stopPolling();
				notification.error({
					message: analysisTitle,
					description: `Unable to load previous ${analysisLabelLower} records.`,
				});
			} finally {
				if (!cancelled && showSpinner) {
					setIsLoading(false);
				}
			}
		};

		void fetchSewerAnalysis(!pendingRecordId, shouldTrackHistorical);

		return () => {
			cancelled = true;
			stopPolling();
		};
	}, [
		analysisLabelLower,
		analysisTitle,
		open,
		refreshKey,
		selectedLocation,
		shouldPollHistorical,
		trackedAnalysisId,
		trackedAnalysisName,
	]);

	const filteredRecords = useMemo(() => {
		const { from, to } = selectedPeriodRange ?? {};
		const minDate = dayjs(from).startOf('day');
		const maxDate = dayjs(to).endOf('day');
		const dateFilteredRecords = !from || !to
			? records
			: records.filter((record) => {
					if (!record.createdAt) return true;
					const parsedDate = dayjs(record.createdAt);
					if (!parsedDate.isValid()) return true;
					return (
						(parsedDate.isAfter(minDate) || parsedDate.isSame(minDate)) &&
						(parsedDate.isBefore(maxDate) || parsedDate.isSame(maxDate))
					);
			  });
		const sortedDateFilteredRecords = sortRecordsNewestFirst(dateFilteredRecords);

		const normalizedTrackedName = normalizeModelName(trackedAnalysisName);
		const activeTrackedRecord = sortedDateFilteredRecords.find(
			(record) =>
				isRecordInProgress(record) &&
				normalizedTrackedName &&
				normalizeModelName(record.title) === normalizedTrackedName,
		);

		if (!activeTrackedRecord) {
			const activeInProgressRecord =
				sortedDateFilteredRecords.find(
					(record) =>
						isRecordInProgress(record) &&
						((trackedAnalysisId && record.id === trackedAnalysisId) ||
							isTemporaryTrackedRecord(record) ||
							(normalizedTrackedName &&
								normalizeModelName(record.title) === normalizedTrackedName)),
				) ?? null;

			if (!activeInProgressRecord) {
				return sortedDateFilteredRecords;
			}

			return [
				activeInProgressRecord,
				...sortedDateFilteredRecords.filter(
					(record) => record.panelKey !== activeInProgressRecord.panelKey,
				),
			];
		}

		const visibleRecords = sortedDateFilteredRecords.filter(
			(record) =>
				record.panelKey === activeTrackedRecord.panelKey ||
				normalizeModelName(record.title) !== normalizedTrackedName,
		);

		return [
			activeTrackedRecord,
			...visibleRecords.filter((record) => record.panelKey !== activeTrackedRecord.panelKey),
		];
	}, [records, selectedPeriodRange, trackedAnalysisId, trackedAnalysisName]);

	useEffect(() => {
		if (!open) {
			hasAutoOpenedDefaultRecordRef.current = false;
			return;
		}

		if (!filteredRecords.length) {
			return;
		}

		const hasActiveRecordInList = filteredRecords.some(
			(record) => getRecordPanelKey(record) === activeCollapseKey,
		);

		if (!hasAutoOpenedDefaultRecordRef.current) {
			setActiveCollapseKey(getRecordPanelKey(filteredRecords[0]));
			hasAutoOpenedDefaultRecordRef.current = true;
			return;
		}

		if (activeCollapseKey && !hasActiveRecordInList) {
			setActiveCollapseKey(getRecordPanelKey(filteredRecords[0]));
		}
	}, [activeCollapseKey, filteredRecords, open]);

	const columns = useMemo(
		() => [
			{
				title: 'Parameter',
				dataIndex: 'parameter',
				key: 'parameter',
				width: '70%',
				render: (value: string, record: SewerAnalysisRecord['parameters'][number]) => (
					<span style={{ fontWeight: record.value ? 400 : 600 }}>{value}</span>
				),
			},
			{
				title: 'Value',
				dataIndex: 'value',
				key: 'value',
				align: 'right' as const,
				width: '30%',
			},
		],
		[],
	);

	const handleDelete = async (record: SewerAnalysisRecord) => {
		try {
			setDeletingRecordId(record.id);
			await keplerService.deleteSewerAnalysis(record.keplerAnimationId ?? record.id);

			let nextRecords: SewerAnalysisRecord[] = [];
			setRecords((current) => {
				nextRecords = current.filter((item) => item.panelKey !== record.panelKey);
				return nextRecords;
			});

			const nextActiveRecord = nextRecords[0] ?? null;
			setActiveCollapseKey(nextActiveRecord ? getRecordPanelKey(nextActiveRecord) : undefined);
		} catch (error) {
			notification.error({
				message: analysisTitle,
				description: `Unable to delete the ${analysisLabelLower} record.`,
			});
		} finally {
			setDeletingRecordId(null);
		}
	};

	const innerContent = (
		<>
			<div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
				{mode === 'modal' && (
					<div>
						<Typography.Title level={3} style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
							{analysisTitle}
						</Typography.Title>
						<Typography.Text
							style={{
								display: 'block',
								marginTop: 4,
								fontSize: 11,
								fontWeight: 600,
								letterSpacing: 0.2,
								color: '#6B7280',
								textTransform: 'uppercase',
							}}
						>
							View list of previous analysis or run the new one
						</Typography.Text>
					</div>
				)}

				<Button
					type="primary"
					icon={<PlusCircleOutlined rev={undefined} />}
					onClick={onCreateNewAnalysis}
					style={{
						height: 42,
						borderRadius: 999,
						backgroundColor: '#0F4C81',
						borderColor: '#0F4C81',
						fontWeight: 600,
					}}
					block
				>
					Create new analysis
				</Button>

				<div>
					<Typography.Title level={4} style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
						Previous records
					</Typography.Title>
					<Typography.Text style={{ display: 'block', marginTop: 12, marginBottom: 8, color: '#6B7280' }}>
						Filter by Period
					</Typography.Text>
					<DatePicker
						key={datePickerResetKey}
						advanced
						includeTime
						defaultValue={datePickerDefaultValue as any}
						keepOpenUntilApply
						label="Filter by Period"
						mode="range"
						onApply={(range) => {
							const normalizedRange = normalizeDatePickerRange(range);
							if (!normalizedRange.from || !normalizedRange.to) return;
							setSelectedPeriodRange(normalizedRange);
						}}
						onClear={() => {
							setSelectedPeriodRange(getDefaultPeriodRange());
						}}
						placeholder="Select date range"
						showPresets
						style={{ width: '100%' }}
					/>
				</div>
			</div>

			<div style={{ padding: '0 10px 10px', maxHeight: '52vh', overflowY: 'auto' }}>
				{isLoading ? (
					<div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
						<Spin />
					</div>
				) : filteredRecords.length ? (
					<Collapse
						activeKey={activeCollapseKey}
						accordion
						bordered={false}
						expandIconPosition="end"
						onChange={(key) => {
							setActiveCollapseKey(normalizeCollapseKey(key));
						}}
						items={filteredRecords.map((record) => ({
							key: getRecordPanelKey(record),
							label: <span style={{ fontWeight: 700, color: '#111827' }}>{isRecordInProgress(record) ? 'In Progress' : record.title}</span>,
							children: (
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: 16,
										maxHeight: '48vh',
										overflowY: 'auto',
										paddingRight: 6,
									}}
								>
									{isRecordInProgress(record) ? (
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												gap: 10,
												padding: '12px 14px',
												borderRadius: 12,
												backgroundColor: '#F8FAFC',
											}}
										>
											<Spin size="small" />
											<Typography.Text
												style={{ fontSize: 13, fontWeight: 600, color: '#0F4C81' }}
											>
												Generating Report
											</Typography.Text>
										</div>
									) : null}
									{shouldShowAnalysisCompletedMessage(record) ? (
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												gap: 8,
												padding: '14px 16px',
												borderRadius: 12,
												backgroundColor: '#E6F6EE',
												border: '1px solid #37C99A',
											}}
										>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: 10,
												}}
											>
												<CheckCircleFilled
													rev={undefined}
													style={{ color: '#22C58B', fontSize: 18 }}
												/>
												<Typography.Text
													style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}
												>
													REPORT IS GENERATED
												</Typography.Text>
											</div>
											{formatRecordDateTime(record.createdAt) ? (
												<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
													<ClockCircleOutlined
														rev={undefined}
														style={{ color: '#111827', fontSize: 14 }}
													/>
													<Typography.Text
														style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}
													>
														{formatRecordDateTime(record.createdAt)}
													</Typography.Text>
												</div>
											) : null}
										</div>
									) : null}
									{shouldShowAnalysisFailedMessage(record) ? (
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												gap: 8,
												padding: '14px 16px',
												borderRadius: 12,
												backgroundColor: '#FDECEC',
												border: '1px solid #F04438',
											}}
										>
											<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
												<CloseCircleFilled
													rev={undefined}
													style={{ color: '#F04438', fontSize: 18 }}
												/>
												<Typography.Text
													style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}
												>
													FAILED
												</Typography.Text>
											</div>
											{formatRecordDateTime(record.createdAt) ? (
												<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
													<ClockCircleOutlined
														rev={undefined}
														style={{ color: '#111827', fontSize: 14 }}
													/>
													<Typography.Text
														style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}
													>
														{formatRecordDateTime(record.createdAt)}
													</Typography.Text>
												</div>
											) : null}
										</div>
									) : null}
									{!isRecordInProgress(record) ? (
										<>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													flexWrap: 'wrap',
													gap: 34,
													padding: '4px 0',
												}}
											>
												<Button
													type="link"
													icon={<EyeOutlined rev={undefined} />}
													onClick={() => {
														const viewUrl = getRecordViewUrl(record);
														if (viewUrl) {
															window.open(viewUrl, '_blank', 'noopener,noreferrer');
														}
													}}
													disabled={!getRecordViewUrl(record)}
													style={{
														height: 'auto',
														padding: 0,
														color: '#0F4C81',
														fontWeight: 600,
													}}
												>
													View
												</Button>
												{!isWaterAnalysis && (
													<Button
														type="link"
														icon={<DownloadOutlined rev={undefined} />}
														onClick={() => {
															const excelUrl = getRecordExcelUrl(record);
															if (excelUrl) {
																window.open(excelUrl, '_blank', 'noopener,noreferrer');
															}
														}}
														style={{
															height: 'auto',
															padding: 0,
															color: '#0F4C81',
															fontWeight: 600,
														}}
													>
														Export
													</Button>
												)}
												<Button
													type="link"
													icon={<DeleteOutlined rev={undefined} />}
													onClick={() => {
														void handleDelete(record);
													}}
													loading={deletingRecordId === record.id}
													disabled={
														isRecordInProgress(record) ||
														(deletingRecordId !== null && deletingRecordId !== record.id)
													}
													style={{
														height: 'auto',
														padding: 0,
														color: '#EF4444',
														fontWeight: 600,
													}}
												>
													Delete
												</Button>
											</div>

											{!isWaterAnalysis && formatRecordDateTime(record.createdAt) ? (
												<Table
													rowKey="key"
													pagination={false}
													size="small"
													columns={columns}
													dataSource={record.parameters}
												/>
											) : null}
										</>
									) : null}
								</div>
							),
						}))}
					/>
				) : (
					<Empty
						description={`No ${analysisLabelLower} records found`}
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				)}
			</div>
		</>
	);

	if (mode === 'panel') {
		return innerContent;
	}

	return (
		<Modal
			open={open}
			onCancel={onCancel}
			maskClosable={false}
			keyboard={false}
			footer={null}
			zIndex={1000}
			wrapClassName="sewer-analysis-launcher-modal-wrap"
			width={420}
			closeIcon={<span style={{ fontSize: 22, color: '#111827' }}>×</span>}
			style={{ top: 88, marginLeft: 'auto', marginRight: 16, paddingBottom: 0 }}
			bodyStyle={{ padding: 0, maxHeight: '88vh', overflow: 'hidden', borderRadius: 16 }}
			maskStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.14)', pointerEvents: 'none' }}
			destroyOnClose
		>
			{innerContent}
		</Modal>
	);
}
